/**
 * Same-name ledger rows, grouped for reading.
 *
 * The server returns one row per organization that bound a name (plus this
 * organization's own unbound row when it holds nothing itself) — by design, see
 * values.service.ts `ledgerFor`. Rendered flat they read as duplicates. Here the
 * page organization's own row anchors each name (own bound → own unbound →
 * inherited → whatever came first) and every other organization's row nests
 * beneath it, ordered by path and indented by depth relative to the page org.
 * Nothing about what a row IS changes: actions still act as the row's org.
 */
import { comparePaths } from './org-tree';

import type { ProxyVaultLedgerEntry, TenantResponse } from '../../lib/api-client';

type OrgLike = Pick<TenantResponse, 'id'> &
  Partial<Pick<TenantResponse, 'path' | 'depth' | 'external_id' | 'name'>>;

export interface NestedLedgerRow {
  entry: ProxyVaultLedgerEntry;
  /** Indent level under the anchor: depth relative to the page organization, at least 1. */
  level: number;
}

export interface LedgerGroup {
  anchor: ProxyVaultLedgerEntry;
  nested: NestedLedgerRow[];
}

const MAX_LEVEL = 8;

export function groupLedger(
  values: readonly ProxyVaultLedgerEntry[],
  pageOrg: string,
  tenants: readonly OrgLike[],
): LedgerGroup[] {
  const byId = new Map(tenants.map((t) => [t.id, t] as const));
  const pathOf = (id: string | null): string | null =>
    id === null ? null : (byId.get(id)?.path ?? null);
  const depthOf = (id: string | null): number | null =>
    id === null ? null : (byId.get(id)?.depth ?? null);
  const labelOf = (e: ProxyVaultLedgerEntry): string =>
    e.tenant_external_id ?? e.tenant_name ?? e.tenant_id ?? '';

  const baseDepth = depthOf(pageOrg);
  const levelOf = (e: ProxyVaultLedgerEntry): number => {
    const d = depthOf(e.tenant_id);
    if (d === null || baseDepth === null) return 1;
    return Math.min(MAX_LEVEL, Math.max(1, d - baseDepth));
  };

  // Known organizations first, in hierarchy order; unknown ones after, by label.
  const compare = (a: ProxyVaultLedgerEntry, b: ProxyVaultLedgerEntry): number => {
    const pa = pathOf(a.tenant_id);
    const pb = pathOf(b.tenant_id);
    if (pa !== null && pb !== null) return comparePaths(pa, pb);
    if (pa !== null || pb !== null) return pa !== null ? -1 : 1;
    return labelOf(a).localeCompare(labelOf(b));
  };

  // Map insertion order = first appearance in the server's response.
  const byName = new Map<string, ProxyVaultLedgerEntry[]>();
  for (const e of values) {
    const rows = byName.get(e.name);
    if (rows) rows.push(e);
    else byName.set(e.name, [e]);
  }

  const groups: LedgerGroup[] = [];
  for (const rows of byName.values()) {
    if (rows.length === 1) {
      groups.push({ anchor: rows[0]!, nested: [] });
      continue;
    }
    const anchor =
      rows.find((r) => r.tenant_id === pageOrg) ??
      rows.find((r) => r.state === 'unbound') ??
      rows.find((r) => r.inherited) ??
      rows[0]!;
    const nested = rows
      .filter((r) => r !== anchor)
      .sort(compare)
      .map((entry) => ({ entry, level: levelOf(entry) }));
    groups.push({ anchor, nested });
  }
  return groups;
}
